import {
  ScanLine,
  Coins,
  TrendingUp,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { Reveal } from "../ui/Reveal";
import { SectionHeading } from "../ui/SectionHeading";
import { Spark } from "../ui/decor";
import { HOW_IT_WORKS } from "../../content/site";

const ICONS: Record<string, LucideIcon> = {
  scan: ScanLine,
  coins: Coins,
  trending: TrendingUp,
  wallet: Wallet,
};

/** One flat pastel per step so the row reads like a hand of colour swatches. */
const PASTELS = ["bg-teal-mist", "bg-sun-pale", "bg-violet-mist", "bg-coral-pale"];

export function HowItWorks() {
  return (
    <section
      id="how-it-works"
      className="relative overflow-hidden bg-cream py-20 sm:py-28"
    >
      <div className="container-x relative">
        {/* playful accents flanking the heading */}
        <Spark className="absolute -top-4 right-6 hidden rotate-12 lg:block" />
        <Spark
          fill="#CCFBF1"
          className="absolute left-2 top-24 hidden h-6 w-6 -rotate-12 lg:block"
        />

        <SectionHeading
          eyebrow="How it works"
          title={
            <>
              From scale to spendable in{" "}
              <span className="marker">four moves</span>
            </>
          }
          sub="No smart scale, no calorie-counting marathon. Weigh in, earn, watch it grow, spend it."
        />

        <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:mt-20 lg:grid-cols-4 lg:gap-6">
          {HOW_IT_WORKS.map((item, i) => {
            const Icon = ICONS[item.icon] ?? ScanLine;
            const tilt = i % 2 === 0 ? "rotate-1" : "-rotate-1";
            const stagger = i % 2 === 1 ? "lg:translate-y-4" : "";
            return (
              <Reveal key={item.step} delay={i} className={stagger}>
                <div
                  className={`relative h-full overflow-hidden rounded-[1.75rem] border-[3px] border-navy p-7 shadow-pop transition-transform duration-200 ease-out hover:-translate-y-1 hover:rotate-0 ${PASTELS[i % PASTELS.length]} ${tilt}`}
                >
                  {/* giant ghost step number */}
                  <span
                    aria-hidden
                    className="display text-outline pointer-events-none absolute -right-2 -top-5 select-none text-7xl leading-none opacity-60"
                  >
                    {item.step}
                  </span>

                  <div className="grid h-12 w-12 place-items-center rounded-full border-[3px] border-navy bg-white">
                    <Icon className="h-6 w-6 text-navy" strokeWidth={2.25} />
                  </div>

                  <h3 className="display mt-6 text-2xl text-navy">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy/75">
                    {item.body}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
